import { LayoutServiceData } from '@sitecore-jss/sitecore-jss-nextjs';

type CardEntry = Record<string, unknown> & { id: string };

type NavObj = Record<string, unknown>;

type CleanupConfig = {
  keys?: string[];
  slimTitle?: boolean;
  children?: { key: string; fn: (item: NavObj) => void }[];
};

const REMOVE = {
  submenuItem: ['id', 'url', 'displayName'],
  category: ['id', 'name', 'url', 'displayName'],
  link: ['id', 'name', 'url', 'displayName', 'CustomCssClass'],
  majorLink: [
    'id',
    'name',
    'url',
    'displayName',
    'CustomCssClass',
    'ContentReferences',
    'ContentReferencesShared',
  ],
  card: ['name', 'url', 'displayName', 'OpenGraphImage'],
};

const CARD_PRESERVE_KEYS = new Set(['Description']);
const LINK_FIELD_KEYS = new Set(['Link', 'backgroundImageExternalLink']);

function cleanupData(config: CleanupConfig): (obj: NavObj) => void {
  return (obj) => {
    if (config.keys?.length) {
      for (const key of config.keys) delete obj[key];
    }
    if (config.slimTitle) {
      const title = obj.Title as NavObj | null | undefined;
      const td = obj.TitleDictionary as NavObj | null | undefined;

      if (!title?.value && td) {
        obj.TitleDictionary = { Phrase: { value: (td.Phrase as NavObj | undefined)?.value } };
      } else {
        delete obj.TitleDictionary;
      }
    }
    for (const { key, fn } of config.children ?? []) {
      (obj[key] as NavObj[] | undefined)?.forEach(fn);
    }
  };
}

const cleanupSubmenuItem = cleanupData({
  keys: REMOVE.submenuItem,
  slimTitle: true,
  children: [
    {
      key: 'MajorLinksCategories',
      fn: cleanupData({
        keys: REMOVE.category,
        children: [
          {
            key: 'MajorLinks',
            fn: cleanupData({
              keys: REMOVE.majorLink,
              slimTitle: true,
              children: [
                {
                  key: 'MajorSublinks',
                  fn: cleanupData({
                    keys: REMOVE.link,
                    slimTitle: true,
                  }),
                },
              ],
            }),
          },
        ],
      }),
    },
    {
      key: 'MinorLinksCategories',
      fn: cleanupData({
        keys: REMOVE.category,
        children: [
          {
            key: 'MinorLinks',
            fn: cleanupData({
              keys: REMOVE.link,
              slimTitle: true,
            }),
          },
        ],
      }),
    },
  ],
});

const cleanupCard = cleanupData({ keys: REMOVE.card });

function extractCards(items: NavObj[]): Record<string, CardEntry> {
  const cards: Record<string, CardEntry> = {};

  for (const item of items) {
    const categories = item.MajorLinksCategories as NavObj[] | undefined;
    if (!categories) continue;

    for (const category of categories) {
      const majorLinks = category.MajorLinks as NavObj[] | undefined;
      if (!majorLinks) continue;

      for (const majorLink of majorLinks) {
        const inlineCards = majorLink.Cards as CardEntry[] | undefined;
        if (!inlineCards?.length) continue;

        majorLink.cardIds = inlineCards.map((card) => {
          cleanupCard(card as NavObj);
          cards[card.id] = card;
          return card.id;
        });
        delete majorLink.Cards;
      }
    }
  }

  return cards;
}

function optimizeValues(obj: NavObj, preserveKeys?: ReadonlySet<string>): void {
  if (!obj || typeof obj !== 'object') return;

  for (const key of Object.keys(obj)) {
    if (preserveKeys?.has(key)) continue;

    const value = obj[key];

    if (LINK_FIELD_KEYS.has(key) && value && typeof value === 'object' && !Array.isArray(value)) {
      const linkValue = (value as NavObj).value as NavObj | undefined;
      if (linkValue && typeof linkValue === 'object') {
        (value as NavObj).value = { href: linkValue.href };
      }
    }

    if (value === null || value === undefined || value === '') {
      delete obj[key];
    } else if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item && typeof item === 'object') optimizeValues(item as NavObj);
      });
      if (value.length === 0) delete obj[key];
    } else if (typeof value === 'object') {
      optimizeValues(value as NavObj);
      if (Object.keys(value as NavObj).length === 0) delete obj[key];
    }
  }
}

export function optimizeHeaderFields(fields: NavObj): void {
  const items = fields.items as NavObj[] | undefined;
  if (!items?.length) return;

  fields.cards = extractCards(items);
  items.forEach(cleanupSubmenuItem);
  items.forEach((item) => optimizeValues(item));
  Object.values(fields.cards as Record<string, CardEntry>).forEach((card) =>
    optimizeValues(card as NavObj, CARD_PRESERVE_KEYS)
  );
}

export function optimizeHeaderNavigationData(layoutData: LayoutServiceData): void {
  const placeholders = layoutData.sitecore.route?.placeholders;
  if (!placeholders) return;

  for (const key of Object.keys(placeholders)) {
    const slot = placeholders[key];
    if (!Array.isArray(slot)) continue;

    for (const rendering of slot) {
      const r = rendering as unknown as NavObj;
      if (r.componentName !== 'Header') continue;

      const fields = r.fields as NavObj | undefined;
      if (!fields) continue;

      optimizeHeaderFields(fields);
      return;
    }
  }
}
